import type { RouteObject } from 'react-router';

import { Outlet } from 'react-router';
import { lazy, Suspense } from 'react';

import { CONFIG } from 'src/global-config';
import { DashboardLayout } from 'src/layouts/dashboard';

import { LoadingScreen } from 'src/components/loading-screen';

import { OrderCreateView } from 'src/sections/orders/view/order-create-view';

import { AuthGuard, RoleBasedGuard } from 'src/auth/guard';

import { AdminRoleEnum } from 'src/types/user';

import { usePathname } from '../hooks';

// ----------------------------------------------------------------------

const IndexPage = lazy(() => import('src/pages/dashboard/dashboard'));
// Media
const MediasPage = lazy(() => import('src/pages/dashboard/medias/list'));
const MediaDetailsPage = lazy(() => import('src/pages/dashboard/medias/details'));
const NewMediaPage = lazy(() => import('src/pages/dashboard/medias/new'));

//Categories
const CategoriesPage = lazy(() => import('src/pages/dashboard/medias/categories'));

//Media Packs
const MediaPacksPage = lazy(() => import('src/pages/dashboard/medias/packs/list'));
const MediaPackNewPage = lazy(() => import('src/pages/dashboard/medias/packs/new'));
const MediaPackDetailsPage = lazy(() => import('src/pages/dashboard/medias/packs/[id]/index'));
const MediaPackEditPage = lazy(() => import('src/pages/dashboard/medias/packs/[id]/edit'));

//Order
const OrdersPage = lazy(() => import('src/pages/dashboard/orders/list'));
const OrderDetailsPage = lazy(() => import('src/pages/dashboard/orders/details'));
const OrderExternalsPage = lazy(() => import('src/pages/dashboard/orders/externals/list'));
const OrderExternalEditPage = lazy(() => import('src/pages/dashboard/orders/externals/edit'));
const OrderExternalPendingPayment = lazy(
  () => import('src/pages/dashboard/orders/externals/pending-payment')
);

//Invoice
const InvoicesPage = lazy(() => import('src/pages/dashboard/invoices/list'));
const InvoicesLLCPage = lazy(() => import('src/pages/dashboard/invoices/llc'));
const InvoiceCreatePage = lazy(() => import('src/pages/dashboard/invoices/new'));
const InvoiceDetailsPage = lazy(() => import('src/pages/dashboard/invoices/details'));

//Outreach
const OutreachPage = lazy(() => import('src/pages/dashboard/outreach/list'));
const OutreachExtensionPage = lazy(() => import('src/pages/dashboard/outreach/extension'));
const KiwosanMediaPage = lazy(() => import('src/pages/dashboard/kiwosan-media/list'));

//Outreach Media Discovery
const OutreachDiscoveryListPage = lazy(() => import('src/pages/dashboard/outreach-discovery/list'));
const OutreachDiscoveryDetailPage = lazy(() => import('src/pages/dashboard/outreach-discovery/detail'));
const OutreachDiscoveryTemplatesPage = lazy(() => import('src/pages/dashboard/outreach-discovery/templates'));
const OutreachInternalPage = lazy(() => import('src/pages/dashboard/outreach-discovery/internal-outreach'));
const OutreachSuppressionsPage = lazy(() => import('src/pages/dashboard/outreach-discovery/suppressions'));
const OutreachTechnicalPage = lazy(() => import('src/pages/dashboard/outreach-discovery/technical'));

//Billing
const PlatformPage = lazy(() => import('src/pages/dashboard/billing/platforms/list'));
const PlatformDetailsPage = lazy(() => import('src/pages/dashboard/billing/platforms/details'));
const NewPlatformPage = lazy(() => import('src/pages/dashboard/billing/platforms/new'));

//Customer
const CustomersPage = lazy(() => import('src/pages/dashboard/customers/list'));
const CustomerDetailsPage = lazy(() => import('src/pages/dashboard/customers/details'));
const NewCustomerPage = lazy(() => import('src/pages/dashboard/customers/new'));
const CustomerImportMediaPage = lazy(() => import('src/pages/dashboard/customers/import-media'));

//Coupons
const CouponsPage = lazy(() => import('src/pages/dashboard/tools/coupons'));

//Calendar
const CalendarPage = lazy(() => import('src/pages/dashboard/tools/calendar'));

//Media Proposals
const MediaProposalsPage = lazy(() => import('src/pages/dashboard/tools/media-proposals/list'));
const MediaProposalCreatePage = lazy(() => import('src/pages/dashboard/tools/media-proposals/new'));

//Profit Management
const ProfitManagementPage = lazy(() => import('src/pages/dashboard/tools/profit-management'));

//Expense Invoices
const ExpenseInvoicesPage = lazy(() => import('src/pages/dashboard/invoices/expenses/list'));
const ExpenseInvoiceCreatePage = lazy(() => import('src/pages/dashboard/invoices/expenses/new'));
const ExpenseInvoiceDetailsPage = lazy(
  () => import('src/pages/dashboard/invoices/expenses/details')
);

//Payment Requests
const PaymentRequestsPage = lazy(() => import('src/pages/dashboard/invoices/payment-requests/list'));

//Admin Users
const AdminUsersPage = lazy(() => import('src/pages/dashboard/admin-users/list'));
const AdminUserCreatePage = lazy(() => import('src/pages/dashboard/admin-users/new'));
const AdminUserDetailsPage = lazy(() => import('src/pages/dashboard/admin-users/details'));
const PendingJournalistsPage = lazy(() => import('src/pages/dashboard/pending-journalists/list'));

//Banners
const BannersPage = lazy(() => import('src/pages/dashboard/banners/list'));
const AlertasPage = lazy(() => import('src/pages/dashboard/alertas/list'));
const AgenteIaPage = lazy(() => import('src/pages/dashboard/agente-ia/list'));
const AgenteIaLogsPage = lazy(() => import('src/pages/dashboard/agente-ia/logs'));
const AgenteIaSupportPage = lazy(() => import('src/pages/dashboard/agente-ia/support'));
const AgenteIaGapsPage = lazy(() => import('src/pages/dashboard/agente-ia/gaps'));
const AgenteIaEvalsPage = lazy(() => import('src/pages/dashboard/agente-ia/evals'));
const ReputationPage = lazy(() => import('src/pages/dashboard/reputation/list'));
const BannerNewPage = lazy(() => import('src/pages/dashboard/banners/new'));
const BannerDetailsPage = lazy(() => import('src/pages/dashboard/banners/details'));

//Logs
const LogsPage = lazy(() => import('src/pages/dashboard/logs/list'));
const LiveTrackerPage = lazy(() => import('src/pages/dashboard/live-tracker'));
//Fast Indexer (Indexador)
const FastIndexerPage = lazy(() => import('src/pages/dashboard/fast-indexer/list'));

//Backlink Monitor
const BacklinkMonitorPage = lazy(() => import('src/pages/dashboard/backlink-monitor/list'));
const BacklinkMonitorUserPage = lazy(() => import('src/pages/dashboard/backlink-monitor/user'));
const BacklinkMonitorTechnicalPage = lazy(() => import('src/pages/dashboard/backlink-monitor/technical'));

//Status backups
const BackupsPage = lazy(() => import('src/pages/dashboard/backups/status'));

//Hangfire
const HangfirePage = lazy(() => import('src/pages/dashboard/hangfire'));

//Salud del Sistema
const SystemHealthPage = lazy(() => import('src/pages/dashboard/system-health'));
const QuickReplyTemplatesPage = lazy(() => import('src/pages/dashboard/quick-reply-templates'));

//GMB Review Packs
const GMBReviewPackCreatePage = lazy(
  () => import('src/pages/dashboard/tools/profit-management/gmb/new')
);
const GMBReviewPackEditPage = lazy(
  () => import('src/pages/dashboard/tools/profit-management/gmb/[id]/edit')
);

//Press Release Packs
const PressReleasePackEditPage = lazy(
  () => import('src/pages/dashboard/tools/profit-management/press-release/[id]/edit')
);

//GMB Booster Packs
const GMBBoosterPackEditPage = lazy(
  () => import('src/pages/dashboard/tools/profit-management/gmb-booster/[id]/edit')
);

//Local Citation Packs
const LocalCitationPackEditPage = lazy(
  () => import('src/pages/dashboard/tools/profit-management/local-citation/[id]/edit')
);

//Mailgun
const MailgunTemplatesPage = lazy(() => import('src/pages/dashboard/tools/mailgun/list'));
const MailgunTemplateNewPage = lazy(() => import('src/pages/dashboard/tools/mailgun/new'));
const MailgunTemplateEditPage = lazy(() => import('src/pages/dashboard/tools/mailgun/edit'));

//Category Analyzer
const CategoryAnalyzerPage = lazy(() => import('src/pages/dashboard/tools/category-analyzer'));

//Newsletter Ads Requests
const NewsletterAdsRequestsPage = lazy(() => import('src/pages/dashboard/tools/newsletter-ads-requests/list'));
const NewsletterAdsRequestDetailsPage = lazy(() => import('src/pages/dashboard/tools/newsletter-ads-requests/[id]/index'));
const ApiKeysPage = lazy(() => import('src/pages/dashboard/tools/api-keys/list'));
const ComparatorApiKeysPage = lazy(() => import('src/pages/dashboard/tools/comparator-api-keys/list'));
const LanguageConfigPage = lazy(() => import('src/pages/dashboard/tools/language-config'));
const NewsletterCatalogPage = lazy(() => import('src/pages/dashboard/tools/newsletter-catalog'));

//Managed Services
const ManagedServicesPage = lazy(() => import('src/pages/dashboard/managed-services/list'));
const ListiclesPage = lazy(() => import('src/pages/dashboard/listicles/list'));
const ManagedServiceEditPage = lazy(() => import('src/pages/dashboard/managed-services/edit'));

//Editor Management
const PendingMediasPage = lazy(() => import('src/pages/dashboard/editor-management/pending-medias'));
const EditorNewslettersPage = lazy(() => import('src/pages/dashboard/editor-management/newsletters/list'));
const EditorNewsletterNewPage = lazy(() => import('src/pages/dashboard/editor-management/newsletters/new'));
const EditorNewsletterDetailsPage = lazy(() => import('src/pages/dashboard/editor-management/newsletters/[id]/index'));
const EditorNewsletterEditPage = lazy(() => import('src/pages/dashboard/editor-management/newsletters/[id]/edit'));

//Moderation
const MessagesModerationPage = lazy(() => import('src/pages/dashboard/moderation/messages'));
const BannedWordsPage = lazy(() => import('src/pages/dashboard/moderation/banned-words'));

//MCP
const McpRestrictionsPage = lazy(() => import('src/pages/dashboard/mcp/restrictions'));

//Vitalinks
const VitalinksPage = lazy(() => import('src/pages/dashboard/vitalinks/list'));

//Statistics
const StatisticsBillingPage = lazy(() => import('src/pages/dashboard/statistics/billing'));
const StatisticsPerformancePage = lazy(() => import('src/pages/dashboard/statistics/performance'));
const StatisticsProductPage = lazy(() => import('src/pages/dashboard/statistics/product'));
const StatisticsCustomerPage = lazy(() => import('src/pages/dashboard/statistics/customer'));
const StatisticsEditorsPage = lazy(() => import('src/pages/dashboard/statistics/editors'));



function SuspenseOutlet() {
  const pathname = usePathname();
  return (
    <Suspense key={pathname} fallback={<LoadingScreen />}>
      <Outlet />
    </Suspense>
  );
}

const dashboardLayout = () => (
  <DashboardLayout>
    <SuspenseOutlet />
  </DashboardLayout>
);

export const dashboardRoutes: RouteObject[] = [
  {
    path: 'dashboard',
    element: CONFIG.auth.skip ? dashboardLayout() : <AuthGuard>{dashboardLayout()}</AuthGuard>,
    children: [
      {
        element: (
          <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
            <IndexPage />
          </RoleBasedGuard>
        ),
        index: true,
      },
      {
        path: 'medias',
        children: [
          {
            index: true,
            element: (
              <RoleBasedGuard
                allowedRoles={[
                  AdminRoleEnum.Administrator,
                  AdminRoleEnum.OperationsManager,
                  AdminRoleEnum.Supervisor,
                  AdminRoleEnum.Outreach,
                  AdminRoleEnum.EditorManager,
                ]}
              >
                <MediasPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'new',
            element: (
              <RoleBasedGuard
                allowedRoles={[
                  AdminRoleEnum.Administrator,
                  AdminRoleEnum.OperationsManager,
                  AdminRoleEnum.Supervisor,
                  AdminRoleEnum.Outreach,
                ]}
              >
                <NewMediaPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'packs',
            children: [
              {
                index: true,
                element: (
                  <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                    <MediaPacksPage />
                  </RoleBasedGuard>
                ),
              },
              {
                path: 'new',
                element: (
                  <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                    <MediaPackNewPage />
                  </RoleBasedGuard>
                ),
              },
              {
                path: ':id',
                element: (
                  <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                    <MediaPackDetailsPage />
                  </RoleBasedGuard>
                ),
              },
              {
                path: ':id/edit',
                element: (
                  <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                    <MediaPackEditPage />
                  </RoleBasedGuard>
                ),
              },
            ],
          },
          {
            path: ':id',
            element: (
              <RoleBasedGuard
                allowedRoles={[
                  AdminRoleEnum.Administrator,
                  AdminRoleEnum.OperationsManager,
                  AdminRoleEnum.Supervisor,
                  AdminRoleEnum.Outreach,
                  AdminRoleEnum.EditorManager,
                ]}
              >
                <MediaDetailsPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'categories',
            children: [
              {
                index: true,
                element: (
                  <RoleBasedGuard
                    allowedRoles={[
                      AdminRoleEnum.Administrator,
                      AdminRoleEnum.OperationsManager,
                      AdminRoleEnum.Supervisor,
                      AdminRoleEnum.Outreach,
                      AdminRoleEnum.EditorManager,
                    ]}
                  >
                    <CategoriesPage />
                  </RoleBasedGuard>
                ),
              },
            ],
          },
          {
            path: 'import-media',
            element: (
              <RoleBasedGuard
                allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.OperationsManager, AdminRoleEnum.Supervisor]}
              >
                <CustomerImportMediaPage />
              </RoleBasedGuard>
            ),
          },
        ],
      },

      {
        path: 'orders',
        children: [
          {
            index: true,
            element: (
              <RoleBasedGuard
                allowedRoles={[
                  AdminRoleEnum.Administrator,
                  AdminRoleEnum.OperationsManager,
                  AdminRoleEnum.Supervisor,
                  AdminRoleEnum.EditorManager,
                  AdminRoleEnum.Editor,
                ]}
              >
                <OrdersPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'list',
            element: (
              <RoleBasedGuard
                allowedRoles={[
                  AdminRoleEnum.Administrator,
                  AdminRoleEnum.OperationsManager,
                  AdminRoleEnum.Supervisor,
                  AdminRoleEnum.EditorManager,
                  AdminRoleEnum.Editor,
                ]}
              >
                <OrdersPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <RoleBasedGuard
                allowedRoles={[
                  AdminRoleEnum.Administrator,
                  AdminRoleEnum.OperationsManager,
                  AdminRoleEnum.Supervisor,
                  AdminRoleEnum.EditorManager,
                  AdminRoleEnum.Editor,
                ]}
              >
                <OrderDetailsPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'externals',
            children: [
              {
                index: true,
                element: (
                  <RoleBasedGuard
                    allowedRoles={[
                      AdminRoleEnum.Administrator,
                      AdminRoleEnum.OperationsManager,
                      AdminRoleEnum.Supervisor,
                      AdminRoleEnum.EditorManager,
                    ]}
                  >
                    <OrderExternalsPage />
                  </RoleBasedGuard>
                ),
              },
              {
                path: 'list',
                element: (
                  <RoleBasedGuard
                    allowedRoles={[
                      AdminRoleEnum.Administrator,
                      AdminRoleEnum.OperationsManager,
                      AdminRoleEnum.Supervisor,
                      AdminRoleEnum.EditorManager,
                    ]}
                  >
                    <OrderExternalsPage />
                  </RoleBasedGuard>
                ),
              },
              {
                path: 'pending-payments',
                element: (
                  <RoleBasedGuard
                    allowedRoles={[
                      AdminRoleEnum.Administrator,
                      AdminRoleEnum.OperationsManager,
                      AdminRoleEnum.Supervisor,
                      AdminRoleEnum.EditorManager,
                    ]}
                  >
                    <OrderExternalPendingPayment />
                  </RoleBasedGuard>
                ),
              },
              {
                path: 'new',
                element: (
                  <RoleBasedGuard
                    allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.OperationsManager, AdminRoleEnum.Supervisor]}
                  >
                    <OrderCreateView />
                  </RoleBasedGuard>
                ),
              },
              {
                path: ':id/edit',
                element: (
                  <RoleBasedGuard
                    allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.OperationsManager, AdminRoleEnum.Supervisor]}
                  >
                    <OrderExternalEditPage />
                  </RoleBasedGuard>
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'invoices',
        children: [
          {
            index: true,
            element: (
              <RoleBasedGuard
                allowedRoles={[
                  AdminRoleEnum.Administrator,
                  AdminRoleEnum.Accountant,
                ]}
              >
                <InvoicesPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'llc',
            element: (
              <RoleBasedGuard
                allowedRoles={[
                  AdminRoleEnum.Administrator,
                  AdminRoleEnum.Accountant,
                ]}
              >
                <InvoicesLLCPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'new',
            element: (
              <RoleBasedGuard
                allowedRoles={[
                  AdminRoleEnum.Administrator,
                  AdminRoleEnum.Accountant,
                ]}
              >
                <InvoiceCreatePage />
              </RoleBasedGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <RoleBasedGuard
                allowedRoles={[
                  AdminRoleEnum.Administrator,
                  AdminRoleEnum.Accountant,
                ]}
              >
                <InvoiceDetailsPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'expenses',
            children: [
              {
                index: true,
                element: (
                  <RoleBasedGuard
                    allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.OperationsManager, AdminRoleEnum.Accountant]}
                  >
                    <ExpenseInvoicesPage />
                  </RoleBasedGuard>
                ),
              },
              {
                path: 'new',
                element: (
                  <RoleBasedGuard
                    allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.OperationsManager, AdminRoleEnum.Accountant]}
                  >
                    <ExpenseInvoiceCreatePage />
                  </RoleBasedGuard>
                ),
              },
              {
                path: ':id',
                element: (
                  <RoleBasedGuard
                    allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.OperationsManager, AdminRoleEnum.Accountant]}
                  >
                    <ExpenseInvoiceDetailsPage />
                  </RoleBasedGuard>
                ),
              },
            ],
          },
          {
            path: 'payment-requests',
            children: [
              {
                index: true,
                element: (
                  <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.OperationsManager, AdminRoleEnum.Accountant]}>
                    <PaymentRequestsPage />
                  </RoleBasedGuard>
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'outreach',
        children: [
          {
            index: true,
            element: (
              <RoleBasedGuard
                allowedRoles={[
                  AdminRoleEnum.Administrator,
                  AdminRoleEnum.OperationsManager,
                  AdminRoleEnum.Supervisor,
                  AdminRoleEnum.Outreach,
                ]}
              >
                <OutreachPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'extension',
            element: (
              <RoleBasedGuard
                allowedRoles={[
                  AdminRoleEnum.Administrator,
                  AdminRoleEnum.OperationsManager,
                  AdminRoleEnum.Supervisor,
                  AdminRoleEnum.Outreach,
                ]}
              >
                <OutreachExtensionPage />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: 'kiwosan-media',
        element: (
          <RoleBasedGuard
            allowedRoles={[
              AdminRoleEnum.Administrator,
              AdminRoleEnum.OperationsManager,
              AdminRoleEnum.Supervisor,
              AdminRoleEnum.Outreach,
            ]}
          >
            <KiwosanMediaPage />
          </RoleBasedGuard>
        ),
      },
      {
        path: 'outreach-discovery',
        children: [
          {
            index: true,
            element: (
              <RoleBasedGuard
                allowedRoles={[
                  AdminRoleEnum.Administrator,
                  AdminRoleEnum.OperationsManager,
                  AdminRoleEnum.Supervisor,
                  AdminRoleEnum.Outreach,
                ]}
              >
                <OutreachDiscoveryListPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'templates',
            element: (
              <RoleBasedGuard
                allowedRoles={[
                  AdminRoleEnum.Administrator,
                  AdminRoleEnum.OperationsManager,
                  AdminRoleEnum.Supervisor,
                  AdminRoleEnum.Outreach,
                ]}
              >
                <OutreachDiscoveryTemplatesPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'internal-outreach',
            element: (
              <RoleBasedGuard
                allowedRoles={[
                  AdminRoleEnum.Administrator,
                  AdminRoleEnum.OperationsManager,
                  AdminRoleEnum.Supervisor,
                  AdminRoleEnum.Outreach,
                ]}
              >
                <OutreachInternalPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'suppressions',
            element: (
              <RoleBasedGuard
                allowedRoles={[
                  AdminRoleEnum.Administrator,
                  AdminRoleEnum.OperationsManager,
                  AdminRoleEnum.Supervisor,
                  AdminRoleEnum.Outreach,
                ]}
              >
                <OutreachSuppressionsPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'technical',
            element: (
              <RoleBasedGuard
                allowedRoles={[
                  AdminRoleEnum.Administrator,
                  AdminRoleEnum.OperationsManager,
                  AdminRoleEnum.Supervisor,
                  AdminRoleEnum.Outreach,
                ]}
              >
                <OutreachTechnicalPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <RoleBasedGuard
                allowedRoles={[
                  AdminRoleEnum.Administrator,
                  AdminRoleEnum.OperationsManager,
                  AdminRoleEnum.Supervisor,
                  AdminRoleEnum.Outreach,
                ]}
              >
                <OutreachDiscoveryDetailPage />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: 'customers',
        children: [
          {
            index: true,
            element: (
              <RoleBasedGuard
                allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.OperationsManager, AdminRoleEnum.Supervisor]}
              >
                <CustomersPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'list',
            element: (
              <RoleBasedGuard
                allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.OperationsManager, AdminRoleEnum.Supervisor]}
              >
                <CustomersPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <RoleBasedGuard
                allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.OperationsManager, AdminRoleEnum.Supervisor]}
              >
                <CustomerDetailsPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'new',
            element: (
              <RoleBasedGuard
                allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.OperationsManager, AdminRoleEnum.Supervisor]}
              >
                <NewCustomerPage />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: 'billing',
        children: [
          {
            path: 'platforms',
            children: [
              {
                index: true,
                element: (
                  <RoleBasedGuard
                    allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.OperationsManager, AdminRoleEnum.Supervisor]}
                  >
                    <PlatformPage />
                  </RoleBasedGuard>
                ),
              },
              {
                path: ':id',
                element: (
                  <RoleBasedGuard
                    allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.OperationsManager, AdminRoleEnum.Supervisor]}
                  >
                    <PlatformDetailsPage />
                  </RoleBasedGuard>
                ),
              },
              {
                path: 'new',
                element: (
                  <RoleBasedGuard
                    allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.OperationsManager, AdminRoleEnum.Supervisor]}
                  >
                    <NewPlatformPage />
                  </RoleBasedGuard>
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'tools',
        children: [
          {
            index: true,
            path: 'coupons',
            element: (
              <RoleBasedGuard
                allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.OperationsManager, AdminRoleEnum.Supervisor]}
              >
                <CouponsPage />
              </RoleBasedGuard>
            ),
          },
          {
            index: true,
            path: 'calendar',
            element: (
              <RoleBasedGuard
                allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.OperationsManager, AdminRoleEnum.Supervisor]}
              >
                <CalendarPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'media-proposals',
            children: [
              {
                index: true,
                element: (
                  <RoleBasedGuard
                    allowedRoles={[
                      AdminRoleEnum.Administrator,
                      AdminRoleEnum.OperationsManager,
                      AdminRoleEnum.Supervisor,
                      AdminRoleEnum.Outreach,
                    ]}
                  >
                    <MediaProposalsPage />
                  </RoleBasedGuard>
                ),
              },
              {
                path: 'new',
                element: (
                  <RoleBasedGuard
                    allowedRoles={[
                      AdminRoleEnum.Administrator,
                      AdminRoleEnum.OperationsManager,
                      AdminRoleEnum.Supervisor,
                      AdminRoleEnum.Outreach,
                    ]}
                  >
                    <MediaProposalCreatePage />
                  </RoleBasedGuard>
                ),
              },
            ],
          },
          {
            path: 'profit-management',
            children: [
              {
                index: true,
                element: (
                  <RoleBasedGuard
                    allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.OperationsManager, AdminRoleEnum.Supervisor]}
                  >
                    <ProfitManagementPage />
                  </RoleBasedGuard>
                ),
              },
              {
                path: 'gmb',
                children: [
                  {
                    path: 'new',
                    element: (
                      <RoleBasedGuard
                        allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.OperationsManager, AdminRoleEnum.Supervisor]}
                      >
                        <GMBReviewPackCreatePage />
                      </RoleBasedGuard>
                    ),
                  },
                  {
                    path: ':id/edit',
                    element: (
                      <RoleBasedGuard
                        allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.OperationsManager, AdminRoleEnum.Supervisor]}
                      >
                        <GMBReviewPackEditPage />
                      </RoleBasedGuard>
                    ),
                  },
                ],
              },
              {
                path: 'press-release',
                children: [
                  {
                    path: ':id/edit',
                    element: (
                      <RoleBasedGuard
                        allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.OperationsManager, AdminRoleEnum.Supervisor]}
                      >
                        <PressReleasePackEditPage />
                      </RoleBasedGuard>
                    ),
                  },
                ],
              },
              {
                path: 'gmb-booster',
                children: [
                  {
                    path: ':id/edit',
                    element: (
                      <RoleBasedGuard
                        allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.OperationsManager, AdminRoleEnum.Supervisor]}
                      >
                        <GMBBoosterPackEditPage />
                      </RoleBasedGuard>
                    ),
                  },
                ],
              },
              {
                path: 'local-citation',
                children: [
                  {
                    path: ':id/edit',
                    element: (
                      <RoleBasedGuard
                        allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.OperationsManager, AdminRoleEnum.Supervisor]}
                      >
                        <LocalCitationPackEditPage />
                      </RoleBasedGuard>
                    ),
                  },
                ],
              },
            ],
          },
          {
            path: 'mailgun',
            children: [
              {
                index: true,
                element: (
                  <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                    <MailgunTemplatesPage />
                  </RoleBasedGuard>
                ),
              },
              {
                path: 'templates',
                children: [
                  {
                    index: true,
                    element: (
                      <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                        <MailgunTemplatesPage />
                      </RoleBasedGuard>
                    ),
                  },
                  {
                    path: 'new',
                    element: (
                      <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                        <MailgunTemplateNewPage />
                      </RoleBasedGuard>
                    ),
                  },
                  {
                    path: ':name/edit',
                    element: (
                      <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                        <MailgunTemplateEditPage />
                      </RoleBasedGuard>
                    ),
                  },
                ],
              },
            ],
          },
          {
            path: 'category-analyzer',
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                <CategoryAnalyzerPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'api-keys',
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                <ApiKeysPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'comparator-api-keys',
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                <ComparatorApiKeysPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'language-config',
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                <LanguageConfigPage />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: 'admin-users',
        children: [
          {
            // Listado: Administrator y Supervisor (solo lectura para Supervisor).
            index: true,
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.OperationsManager, AdminRoleEnum.Supervisor]}>
                <AdminUsersPage />
              </RoleBasedGuard>
            ),
          },
          {
            // Crear usuario del staff: Administrator y Supervisor.
            path: 'new',
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.OperationsManager, AdminRoleEnum.Supervisor]}>
                <AdminUserCreatePage />
              </RoleBasedGuard>
            ),
          },
          {
            // Ficha: Administrator y Supervisor. Las acciones de escritura (guardar,
            // generar contrasena) se ocultan a Supervisor dentro de la vista.
            path: ':id',
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.OperationsManager, AdminRoleEnum.Supervisor]}>
                <AdminUserDetailsPage />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        // Periodistas pendientes: validar/rechazar es solo Administrator en el backend.
        path: 'pending-journalists',
        children: [
          {
            index: true,
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                <PendingJournalistsPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                <AdminUserDetailsPage />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: 'banners',
        children: [
          {
            index: true,
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                <BannersPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'new',
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                <BannerNewPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                <BannerDetailsPage />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: 'alertas',
        element: (
          <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
            <AlertasPage />
          </RoleBasedGuard>
        ),
      },
      {
        path: 'agente-ia',
        element: (
          <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
            <AgenteIaPage />
          </RoleBasedGuard>
        ),
      },
      {
        path: 'agente-ia/logs',
        element: (
          <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
            <AgenteIaLogsPage />
          </RoleBasedGuard>
        ),
      },
      {
        path: 'agente-ia/support',
        element: (
          <RoleBasedGuard
            allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.OperationsManager, AdminRoleEnum.Supervisor]}
          >
            <AgenteIaSupportPage />
          </RoleBasedGuard>
        ),
      },
      {
        path: 'agente-ia/gaps',
        element: (
          <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
            <AgenteIaGapsPage />
          </RoleBasedGuard>
        ),
      },
      {
        path: 'agente-ia/evals',
        element: (
          <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
            <AgenteIaEvalsPage />
          </RoleBasedGuard>
        ),
      },
      {
        path: 'reputation',
        element: (
          <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
            <ReputationPage />
          </RoleBasedGuard>
        ),
      },
      {
        path: 'live-tracker',
        element: (
          <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
            <LiveTrackerPage />
          </RoleBasedGuard>
        ),
      },
      {
        path: 'logs',
        children: [
          {
            index: true,
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                <LogsPage />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: 'fast-indexer',
        children: [
          {
            index: true,
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                <FastIndexerPage />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: 'backlink-monitor',
        children: [
          {
            index: true,
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                <BacklinkMonitorPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'tecnico',
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                <BacklinkMonitorTechnicalPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: ':userId',
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                <BacklinkMonitorUserPage />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: 'backups',
        children: [
          {
            index: true,
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                <BackupsPage />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: 'hangfire',
        children: [
          {
            index: true,
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                <HangfirePage />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: 'system-health',
        children: [
          {
            index: true,
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                <SystemHealthPage />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: 'quick-reply-templates',
        children: [
          {
            index: true,
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                <QuickReplyTemplatesPage />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: 'managed-services',
        children: [
          {
            index: true,
            element: (
              <RoleBasedGuard
                allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.OperationsManager, AdminRoleEnum.Supervisor]}
              >
                <ManagedServicesPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <RoleBasedGuard
                allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.OperationsManager, AdminRoleEnum.Supervisor]}
              >
                <ManagedServiceEditPage />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: 'listicles',
        children: [
          {
            index: true,
            element: (
              <RoleBasedGuard
                allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.OperationsManager, AdminRoleEnum.Supervisor]}
              >
                <ListiclesPage />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: 'editor-management',
        children: [
          {
            path: 'pending-medias',
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.EditorManager]}>
                <PendingMediasPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'newsletters',
            children: [
              {
                index: true,
                element: (
                  <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.EditorManager]}>
                    <EditorNewslettersPage />
                  </RoleBasedGuard>
                ),
              },
              {
                path: 'new',
                element: (
                  <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.EditorManager]}>
                    <EditorNewsletterNewPage />
                  </RoleBasedGuard>
                ),
              },
              {
                path: 'catalog',
                element: (
                  <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                    <NewsletterCatalogPage />
                  </RoleBasedGuard>
                ),
              },
              {
                path: 'ads-requests',
                children: [
                  {
                    index: true,
                    element: (
                      <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                        <NewsletterAdsRequestsPage />
                      </RoleBasedGuard>
                    ),
                  },
                  {
                    path: ':id',
                    element: (
                      <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                        <NewsletterAdsRequestDetailsPage />
                      </RoleBasedGuard>
                    ),
                  },
                ],
              },
              {
                path: ':id',
                element: (
                  <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.EditorManager]}>
                    <EditorNewsletterDetailsPage />
                  </RoleBasedGuard>
                ),
              },
              {
                path: ':id/edit',
                element: (
                  <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.EditorManager]}>
                    <EditorNewsletterEditPage />
                  </RoleBasedGuard>
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'moderation',
        children: [
          {
            path: 'messages',
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.OperationsManager, AdminRoleEnum.Supervisor]}>
                <MessagesModerationPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'banned-words',
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.OperationsManager, AdminRoleEnum.Supervisor]}>
                <BannedWordsPage />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: 'mcp',
        children: [
          {
            path: 'restrictions',
            element: (
              <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator]}>
                <McpRestrictionsPage />
              </RoleBasedGuard>
            ),
          },
        ],
      },
      {
        path: 'vitalinks',
        element: (
          <RoleBasedGuard allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.OperationsManager, AdminRoleEnum.Supervisor]}>
            <VitalinksPage />
          </RoleBasedGuard>
        ),
      },
      {
        path: 'statistics',
        children: [
          {
            index: true,
            element: (
              <RoleBasedGuard
                allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Accountant]}
              >
                <StatisticsBillingPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'billing',
            element: (
              <RoleBasedGuard
                allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Accountant]}
              >
                <StatisticsBillingPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'performance',
            element: (
              <RoleBasedGuard
                allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Accountant]}
              >
                <StatisticsPerformancePage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'product',
            element: (
              <RoleBasedGuard
                allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Accountant]}
              >
                <StatisticsProductPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'customer',
            element: (
              <RoleBasedGuard
                allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Accountant]}
              >
                <StatisticsCustomerPage />
              </RoleBasedGuard>
            ),
          },
          {
            path: 'editors',
            element: (
              <RoleBasedGuard
                allowedRoles={[AdminRoleEnum.Administrator, AdminRoleEnum.Accountant]}
              >
                <StatisticsEditorsPage />
              </RoleBasedGuard>
            ),
          },
        ],
      },
    ],
  },
];
